// @flow
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Switch, Route, useHistory, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Hidden,
  IconButton,
  Divider,
  CircularProgress,
  Link as LinkButton,
} from '@material-ui/core';
import {
  ArrowBack,
  KeyboardArrowUp,
  KeyboardArrowDown,
  LocalOffer,
} from '@material-ui/icons';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';

import type {
  User,
  Store,
  Product,
  Currency,
  ProductVariant,
  Category,
  Pagination,
  Order,
  SinglePromotion,
} from '../../../entities';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import {usePage} from '../../page/utils';

import {commaFormat} from '../../../utils/number';

import SearchBar from '../../../elements/SearchBar/SearchBar';

import ProductConfigurationModal from './ProductConfigurationModal';
import HorizontalFilter from '../../../elements/HorizontalFilter/HorizontalFilter';
import HorizontalScroll from '../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from '../../../elements/HorizontalFilter/FilterButton/FilterButton';

import {getProductItemOriginalPrice} from '../../../utils/product';
import ManualOrderCheckout from './ManualOrderCheckout';
import {getCustomDomain, useIsFeatureInTier} from '../../../utils/component';
import ProductItem from './ProductItem';
import ShoppingCardItem from './ShoppingCardItem';

import ShoppingCartFooter from './ShoppingCartFooter';

import type {
  ShoppingCart,
  ManualOrderAction,
  ShoppingCartItem,
  ShoppingCartPricesRequest,
} from '../duck';
import PromotionModal from './PromotionModal';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import {AdditionalInformationProvider} from '../../configure/sales-process/context/AdditionalInformationContext';

import type {
  CartSummaryCoupon,
  CartSummaryItem,
  CartSummaryGlobalDiscount,
  CartSummaryItemPromotion,
} from '../../../entities';
import {getProductTaxFactor} from '../../products/utils';
import OrderCreationSuccess from './OrderCreationSuccess';
import {useDebounceEffect, useNearScreen} from '../../../utils/react';

import type {OrderItemProductOption} from '../../../entities';
import {buildCartItemID} from '../utils/build-cart-item-id';

type FilterType = 'category' | 'promotion';

type Props = {
  store?: Store,
  user?: User,
  currency: Currency,
  categories: Category[],
  fetchProducts: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  products?: Product[],
  isFetchingProducts?: boolean,
  pagination?: Pagination,
  shoppingCart: ShoppingCart,
  addShoppingCartItem: (payload: ManualOrderAction) => void,
  addDiscountShoppingCartItem: (payload: ManualOrderAction) => void,
  addGlobalDiscountShoppingCart: (payload: ManualOrderAction) => void,
  setProducts: (payload: ManualOrderAction) => void,
  applyCoupon: (payload: ManualOrderAction) => void,
  removeCoupon: (payload?: ManualOrderAction) => void,
  deleteShoppingCartItem: (payload: ManualOrderAction) => void,
  fetchShoppingCartPrices: (payload: ManualOrderAction) => void,
  itemPrices?: CartSummaryItem[],
  error?: string,
  subTotal?: string,
  total?: string,
  coupon?: CartSummaryCoupon,
  couponDiscount?: string,
  promotions?: CartSummaryItemPromotion[],
  resetState: () => void,
  createOrderSuccess?: boolean,
  order?: Order,
  globalDiscountData?: CartSummaryGlobalDiscount,
  removeGlobalDiscountShoppingCart: (payload: ManualOrderAction) => void,
  storePromotions?: SinglePromotion[],
  fetchPromotions: () => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem 1rem 0 1rem',
    background: '#f8f9fb',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  alert: {
    maxWidth: 1300,
    margin: theme.spacing(1, 4, 0, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(1, 2, 0, 2),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    maxWidth: 1300,
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 4, 2),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 64,
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 4, 4, 4),
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 2, 1),
      marginBottom: 180,
    },
  },
  orderLeftContent: {
    maxHeight: '100%',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  searchContainer: {
    width: '100%',
    padding: theme.spacing(2, 0, 2, 0),
  },
  filters: {
    display: 'flex',
    width: '100%',
    gap: '14px',
  },
  filterDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  filterText: {
    fontSize: '12px',
    color: colors.grey700,
    whiteSpace: 'nowrap',
  },
  filterBtn: {
    fontSize: '14px',
    color: colors.grey800,
  },
  filterBtnActive: {
    color: colors.purple,
    textDecoration: 'underline',
  },
  orderLeftContentData: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(1),
    background: 'white',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  orderRightContent: {
    height: '85vh',
    paddingRight: '.5rem',
    overflowY: 'auto',
    marginTop: '70px',
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
    width: '40%',

    position: 'sticky',
    top: 100,
  },
  orderContent: {
    marginTop: theme.spacing(2),
    width: '100%',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    display: 'flex',
    background: 'white',
    flexDirection: 'column',
    borderRadius: '12px',
    padding: theme.spacing(2),
  },
  sectionTitleSummary: {
    borderRadius: '12px',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  sectionTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: colors.grey900,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  shoppingCartItemsDivider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(1.5, 0, 2, 0),
  },
  categoriesContainer: {
    overflow: 'hidden',
  },
  loadingSpinner: {
    alignSelf: 'center',
    marginTop: theme.spacing(3),
  },
  productList: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    },
    [theme.breakpoints.up('lg')]: {
      gap: '16px',
    },
  },
  product: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    // alignItems: 'center',
    position: 'relative',
  },
  disabledProduct: {
    opacity: 0.5,
    filter: 'grayscale(50%)',
  },
  productImage: {
    borderRadius: '12px',
    height: 104,
    width: 104,
    marginRight: 24,
  },
  productDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    marginRight: 24,
  },
  productName: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: 110,
    },
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  productVariants: {
    borderRadius: '4px',
    backgroundColor: colors.grey200,
    padding: '2px 8px',
    color: colors.grey700,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  productPrice: {
    display: 'flex',
  },
  productPriceNormal: {
    fontWeight: 500,
    fontSize: '1.125rem',
    whiteSpace: 'nowrap',
  },
  productPriceSlashed: {
    marginLeft: theme.spacing(1),
    fontSize: '1.125rem',
    whiteSpace: 'nowrap',
    color: colors.grey500,
    textDecoration: 'line-through',
  },
  productBulkPriceFlag: {
    position: 'absolute',
    top: 0,
    right: 8,
    backgroundColor: '#158CFD',
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '24px',
    width: 'fit-content',
    padding: '2px 8px',
    [theme.breakpoints.down('xs')]: {
      right: 6,
      padding: 6,
      color: '#158CFD',
      background: '#E9F4FF',
    },
  },
  productNoStockFlag: {
    position: 'absolute',
    top: 0,
    right: 8,
    backgroundColor: colors.grey200,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '24px',
    width: 'fit-content',
    padding: '4px 12px',
  },
  productAddButton: {
    alignSelf: 'center',
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      alignSelf: 'flex-end',
    },
  },
  shoppingCartItemsAmount: {
    marginLeft: 'auto',
    backgroundColor: colors.grey200,
    borderRadius: '12px',
    padding: '4px 12px',
    fontWeight: 600,
  },
  shoppingCartItems: {
    margin: theme.spacing(0, -3),
    padding: theme.spacing(0, 3),
    overflow: 'auto',
    height: 'calc(100vh - 64px - 80px - 150px)',
    [theme.breakpoints.up('sm')]: {
      height: 'auto',
      maxHeight: 'calc(100vh - 376px)',
    },
  },
  shoppingCartProductDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    marginRight: -36,
  },
  shoppingCartProductImage: {
    borderRadius: '12px',
    height: 64,
    width: 64,
    marginRight: 16,
  },
  shoppingCartProductName: {
    fontSize: '0.875rem',
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  shoppingCartProductVariant: {
    fontSize: '0.875rem',
    color: colors.grey600,
    fontWeight: 500,
  },
  quantity: {
    marginTop: theme.spacing(1),
  },
  shoppingCartItemActions: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  shoppingCartItemDeleteButton: {
    color: colors.error,
  },
  shoppingCartProductBulkPriceFlag: {
    padding: 6,
    color: '#158CFD',
    background: '#E9F4FF',
    marginRight: 8,
    display: 'flex',
    borderRadius: '20px',
  },
  shoppingCartProductPrice: {
    textAlign: 'right',
  },
  shoppingCartProductPriceNormal: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    color: colors.grey800,
    whiteSpace: 'nowrap',
  },
  shoppingCartProductPriceDiscounted: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey600,
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
  shoppingCartFooter: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  shoppingCartSubtotal: {
    color: colors.grey800,
  },
  shoppingCartTotal: {
    color: colors.grey800,
    fontWeight: 600,
  },
  checkoutButton: {
    width: '100%',
    height: 48,
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  checkoutButtonMobile: {
    width: '100%',
    height: 40,
    fontWeight: 600,
    fontSize: '1rem',
  },
  successContent: {
    marginTop: theme.spacing(2),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    background: 'white',
  },
  successIcon: {
    marginTop: theme.spacing(2),
    color: colors.success,
  },
  successTitle: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontWeight: 600,
  },
  successMessage: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  successButton: {
    marginTop: theme.spacing(4),
    width: 240,
  },
  successReturnButton: {
    margin: theme.spacing(1, 0),
  },
  shoppingCartFooterMobile: {
    position: 'fixed',
    zIndex: 10,
    width: '100%',
    height: '140px',
    background: 'white',
    padding: '16px 20px',
    borderRadius: '16px 16px 0px 0px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    bottom: 64,
    boxShadow: ' 0px -3px 14px rgba(24, 39, 75, 0.12)',
    transition: 'height 0.3s ease-in-out',
  },
  shoppingCartFooterMobileOpen: {
    // height: 'calc(100vh - 80px)',
    zIndex: 10000,
  },
  footerMobileCollapse: {
    padding: '0',
  },
  footerMobileText: {
    color: colors.grey700,
  },

  promotion: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#E4D6FF',
    color: colors.purple,
    fontSize: '14px',
    marginTop: '16px',
    padding: '10px',
    borderRadius: '8px',
  },
  promotionBtn: {
    color: colors.purple,
    padding: '0',
    background: 'transparent',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  snackBarError: {
    backgroundColor: '#FDECEA',
    color: '#A05536',
  },
  copyLinkButton: {
    width: '100%',
    height: 40,
    textTransform: 'none',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    minWidth: 'unset',
  },
  wrapperLink: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 1),
    width: 'auto',
    maxWidth: '100%',
    background: '#F5F0FF',
    border: '1.25px solid rgba(123, 57, 255, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  containerButtonLink: {
    width: '60px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  containerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  copyLink: {
    color: '#500BDB',
  },
  containerCopyLink: {
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    cursor: 'default',
    fontSize: '12px',
    color: '#3A3A49',
  },
  loadingSpinnerWrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default usePage<Props>(ManualOrder, 'Manual Order Creation');

function ManualOrder(props: Props) {
  const {
    store,
    currency,
    categories,
    products,
    isFetchingProducts,
    pagination,
    fetchProducts,
    shoppingCart,
    addShoppingCartItem,
    addDiscountShoppingCartItem,
    removeGlobalDiscountShoppingCart,
    addGlobalDiscountShoppingCart,
    deleteShoppingCartItem,
    fetchShoppingCartPrices,
    itemPrices,
    subTotal,
    total,
    coupon,
    promotions,
    resetState,
    createOrderSuccess,
    order,
    error,
    fetchPromotions,
    storePromotions,
    applyCoupon,
    removeCoupon,
    globalDiscountData,
    couponDiscount,
  } = props;

  const location = useLocation();

  const currentPath = location.pathname;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const {fromRef: nearScreenRef, isNearScreen} = useNearScreen({
    distance: '400px',
    once: false,
  });

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const classes = useStyles();
  const [filterActive, setFilterActive] = useState<FilterType>('category');
  const setCategoryFilter = () => setFilterActive('category');
  const setPromotionFilter = () => setFilterActive('promotion');

  const [shopCartOpen, setShopCartOpen] = useState(false);

  const openShopCart = () => setShopCartOpen(true);
  const closeShopCart = () => setShopCartOpen(false);

  const history = useHistory();

  const hasManualOrderCreationFeature = useIsFeatureInTier(
    'MANUAL_ORDER_CREATION'
  );

  useEffect(() => {
    if (!storePromotions) {
      fetchPromotions();
    }
  }, [filterActive, fetchPromotions, storePromotions]);

  useEffect(() => {
    if (!hasManualOrderCreationFeature) history.replace('/orders');
    return () => resetState();
  }, [hasManualOrderCreationFeature, history, resetState, store]);

  useEffect(() => {
    window.addEventListener('resize', e => {
      setWindowHeight(e.target.innerHeight);
    });
  }, []);

  const [categoriesSelected, setCategoriesSelected] = useState([]);

  const resetCategoriesSelected = () => {
    setCategoriesSelected([]);
  };

  const [promotionSelected, setPromotionSelected] = useState<string[]>([]);
  const resetPromotionSelected = () => setPromotionSelected([]);

  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const openPromotionModal = () => setPromotionModalOpen(true);
  const closePromotionModal = () => setPromotionModalOpen(false);

  // const [page, setPage] = useState(1);
  // const [size, setSize] = useState(16);
  // const [filter, setFilter] = useState('');
  // const [filterObject] = useState<{
  //   enabled?: number,
  //   order?: string,
  //   sort?: string,
  //   stock?: number,
  // }>({});

  const [savedSearchString, setSavedSearchString] = useState('');

  const buildQueryParamsCallback = useCallback(
    (searchString?: string): {[key: string]: any} => {
      const params: {[key: string]: any} = {
        // order: filterObject.order,
        // sort: filterObject.sort,
        enabled: 1,
        // stock: filterObject.stock,
        // page: page - 1,
        // size: size,
        size: 32,
      };
      if (searchString) {
        setSavedSearchString(searchString);
        params.name = searchString;
      }
      if (categoriesSelected.length > 0) {
        params.categories = categoriesSelected.toString();
      }

      if (promotionSelected.length > 0) {
        params.promotions = promotionSelected.toString();
      }

      return params;
    },
    [categoriesSelected, promotionSelected]
  );

  const fetchProductsCallback = useCallback(
    (searchString?: string) => {
      const queryFilter = buildQueryParamsCallback(searchString);
      if (queryFilter) {
        fetchProducts({queryFilter});
      }
    },
    [buildQueryParamsCallback, fetchProducts]
  );

  const shouldFetchOnHome = currentPath === '/orders/manual-order';

  useEffect(() => {
    if (shouldFetchOnHome) fetchProductsCallback();
  }, [fetchProductsCallback, shouldFetchOnHome]);

  const handleCategoryOption = useCallback(
    (id: string) => {
      categoriesSelected.indexOf(id) === -1
        ? setCategoriesSelected([...categoriesSelected, id])
        : setCategoriesSelected(categoriesSelected.filter(e => e !== id));
    },
    [categoriesSelected]
  );

  const handlePromotionOption = (id: string) => {
    const index = promotionSelected.indexOf(id);

    if (index === -1) {
      setPromotionSelected([...promotionSelected, id]);
    } else {
      setPromotionSelected(promotionSelected.filter(e => e !== id));
    }
  };

  const handleAddPage = useCallback(() => {
    if (
      !isFetchingProducts &&
      products &&
      pagination &&
      pagination.total > pagination.size &&
      products.length < pagination.total
    ) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);
      if (queryFilter) {
        fetchProducts({queryFilter, addPage: true});
      }
    }
  }, [
    buildQueryParamsCallback,
    fetchProducts,
    isFetchingProducts,
    pagination,
    products,
    savedSearchString,
  ]);

  useDebounceEffect(
    () => {
      if (!isNearScreen) return;
      handleAddPage();
    },
    250,
    [handleAddPage, isNearScreen]
  );

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [
    productConfiguratorModalOpen,
    setProductConfiguratorModalOpen,
  ] = useState(false);

  const handleAddToCartFromConfigurationModal = (
    product: Product,
    options: {
      productVariant?: ProductVariant,
      productOptions?: OrderItemProductOption[],
      quantity: number,
    }
  ) => {
    const {productVariant, productOptions, quantity} = options;

    addShoppingCartItem({
      product,
      productVariant: productVariant,
      amount: quantity,
      productOptions: productOptions,
    });
    setSelectedProduct(null);
    setProductConfiguratorModalOpen(false);
  };

  const handleModalConfigurationClose = () => {
    setSelectedProduct(null);
    setProductConfiguratorModalOpen(false);
  };

  const handleAddProductToCart = (product: Product, amount) => {
    const hasVariants =
      product.productVariantSetting?.enabled &&
      product.productVariantSetting.variants &&
      product.productVariantSetting.variants.length > 0;

    const hasOptions =
      product.productOptionSetting?.enabled &&
      product.productOptionSetting.groups &&
      product.productOptionSetting.groups.length > 0;
    console.log({hasVariants, hasOptions, product});

    if (hasVariants || hasOptions) {
      setSelectedProduct(product);
      setProductConfiguratorModalOpen(true);
    } else {
      addShoppingCartItem({
        product,
        amount: amount,
      });
    }
  };

  const handleDeleteProductFromCart = (itemId: string) => {
    deleteShoppingCartItem({
      itemId,
    });
  };

  const handleGlobalDiscount = discountData => {
    const discountValue = String(discountData.value);

    const discount =
      discountData.type === 'percentage'
        ? {percentageDiscount: discountValue}
        : {fixedDiscount: discountValue};

    addGlobalDiscountShoppingCart({
      globalDiscount: discount,
    });
  };

  const buildShoppingCartPricesRequest = useCallback(() => {
    if (store) {
      const shoppingCartPricesRequest: ShoppingCartPricesRequest = {
        items: [],
        globalDiscount: shoppingCart.globalDiscount,
      };
      if (shoppingCart && shoppingCart.items) {
        shoppingCart.items.forEach(i =>
          shoppingCartPricesRequest.items.push({
            productId: i.product.id,
            productVariantId: i.productVariant
              ? i.productVariant.id
              : undefined,
            quantity: i.amount,
            discount: i.discount,
            productOptions: i.productOptions,
          })
        );
      }

      if (shoppingCart && shoppingCart.couponCode) {
        shoppingCartPricesRequest.coupon = shoppingCart.couponCode;
      }
      return shoppingCartPricesRequest;
    }
    return {};
  }, [shoppingCart, store]);

  useEffect(() => {
    if (store && shoppingCart) {
      const shoppingCartPricesRequest: ShoppingCartPricesRequest = buildShoppingCartPricesRequest();
      fetchShoppingCartPrices({
        shoppingCartPricesRequest: shoppingCartPricesRequest,
      });
    }
  }, [
    buildShoppingCartPricesRequest,
    fetchShoppingCartPrices,
    shoppingCart,
    store,
  ]);

  useEffect(() => {
    if (!error) return;
    if (!shoppingCart.couponCode) return;

    const timeout = setTimeout(() => {
      removeCoupon();
    }, 3500);

    return () => clearTimeout(timeout);
  }, [error, shoppingCart.couponCode, removeCoupon]);

  const promotionsApplied = promotions && promotions.length ? promotions : [];

  const shoppingCartItems =
    shoppingCart && shoppingCart.items && shoppingCart.items.length ? (
      <div className={classes.shoppingCartItems}>
        {shoppingCart.items.map((item: ShoppingCartItem, i) => {
          let taxFactor = 1;
          let currentStock = 0;
          let promotionsApplied = [];

          if (item.product.stockSetting && item.product.stockSetting.enabled) {
            if (
              item.productVariant &&
              typeof item.productVariant.stock === 'number'
            )
              currentStock = item.productVariant.stock;
            else currentStock = item.product.stockSetting.stock;
          }

          let productPrice = {
            unitPrice: getProductItemOriginalPrice(item),
          };

          const productOptionsNames = [];

          console.log({itemPrices, item});

          if (item && itemPrices && itemPrices.length) {
            const index = itemPrices.findIndex(i => {
              const itemDetail = i.item.detail;
              const itemId = buildCartItemID(
                itemDetail.product?.id,
                itemDetail.productVariant?.id,
                itemDetail.productOptions?.map(option => {
                  return {
                    optionId: option.id,
                    optionGroupId: option.productOptionGroupId,
                  };
                })
              );

              console.log({
                itemId,
                item,
              });

              return item.id === itemId;
            });

            const draftItem = itemPrices[index];
            console.log({index, draftItem});
            if (index >= 0 && draftItem.item.price) {
              productPrice = draftItem.item.price;
              promotionsApplied = draftItem.promotions;
              taxFactor = getProductTaxFactor(draftItem.taxes ?? []);
              if (draftItem?.item?.detail?.productOptions) {
                for (const option of draftItem.item.detail.productOptions) {
                  productOptionsNames.push(option.name);
                }
              }
            }
          }

          const handleSetQuantity = (quantity: number) => {
            addShoppingCartItem({
              product: item.product,
              amount: quantity - item.amount,
              productOptions: item.productOptions,
            });
          };

          const handleAddButton = () => {
            addShoppingCartItem({
              product: item.product,
              productVariant: item.productVariant
                ? item.productVariant
                : undefined,
              amount: 1,
              productOptions: item.productOptions,
            });
          };

          const handleAddDiscount = discountData => {
            const discountValue = String(discountData.value);
            const discount =
              discountData.type === 'percentage'
                ? {percentageDiscount: discountValue}
                : {fixedDiscount: discountValue};

            addDiscountShoppingCartItem({
              product: item.product,
              productVariant: item.productVariant
                ? item.productVariant
                : undefined,
              discount,
            });
          };

          const handleRemoveButton = () => {
            addShoppingCartItem({
              product: item.product,
              productVariant: item.productVariant
                ? item.productVariant
                : undefined,
              amount: -1,
            });
          };

          return (
            <React.Fragment key={`item-${i}`}>
              <ShoppingCardItem
                item={item}
                taxFactor={taxFactor}
                currency={currency}
                currentStock={currentStock}
                productPrice={productPrice}
                setQuantity={handleSetQuantity}
                addHandler={handleAddButton}
                removeHandler={handleRemoveButton}
                discountHandler={handleAddDiscount}
                onRemove={() => handleDeleteProductFromCart(item.id)}
                promotionsApplied={promotionsApplied}
                productOptionsNames={productOptionsNames}
              />
              {i + 1 < shoppingCart.items.length && (
                <Divider className={classes.shoppingCartItemsDivider} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    ) : null;

  const shoppingCartItemsAmount = useMemo(() => {
    if (shoppingCart && shoppingCart.items) {
      let result = 0;
      shoppingCart.items.forEach(i => {
        result += i.amount;
      });
      return result;
    }
  }, [shoppingCart]);

  const handleSeePromotionProducts = (promotionId: string) => {
    if (filterActive !== 'promotion') {
      setPromotionFilter();
    }

    if (promotionId) {
      setPromotionSelected([promotionId]);
    }

    closePromotionModal();
  };

  const handleCouponApply = (couponCode: string) => {
    applyCoupon({
      couponCode,
    });
  };

  const handleCouponRemove = () => {
    removeCoupon();
  };

  const shopCartMobileClasses = `${classes.shoppingCartFooterMobile} ${
    shopCartOpen ? classes.shoppingCartFooterMobileOpen : ''
  }`;

  const productOutOfStock = (product: Product) => {
    if (product.stockSetting && product.stockSetting.enabled) {
      if (
        product.productVariantSetting &&
        product.productVariantSetting.enabled &&
        product.productVariantSetting.variants &&
        product.productVariantSetting.variants.length &&
        product.productVariantSetting.variants.filter(v => v.enabled).length
      ) {
        const enabledVariants = product.productVariantSetting.variants.filter(
          v => v.enabled
        );
        let flag = false;
        for (let productV of enabledVariants) {
          if (productV.stock && productV.stock > 0) flag = true;
        }
        return !flag;
      }
      return product.stockSetting.stock === 0;
    }
    return false;
  };

  const orderLink = useMemo(() => {
    if (!order || !store) return '';
    const preOrder = order.type === 'pre-order';
    const domainStore = getCustomDomain(store);

    if (preOrder) {
      return `https://${domainStore}/checkout/information?po_id=${order.id}&po_token=${order.token}`;
    }
    return `https://${domainStore}/order/${order.id}?token=${order.token}`;
  }, [order, store]);

  return (
    <div className={classes.root}>
      <SnackBar open={!!error} mode="error" message={error} />
      {/* <SnackBar mode="error" message={updateOrderStateErrorMessage} /> */}
      <AlertDueDateTier />
      <Switch>
        <Route exact path="/orders/manual-order">
          <div className={classes.headerWrapper}>
            <Typography className={classes.title}>
              <IconButton
                className={classes.backButton}
                onClick={() => history.push('/orders')}
              >
                <ArrowBack />
              </IconButton>
              Crear Pedido
            </Typography>
          </div>

          <main className={classes.content}>
            <section className={classes.orderLeftContent}>
              {storePromotions && storePromotions.length > 0 && (
                <Hidden smUp>
                  <div className={classes.promotion}>
                    <LocalOffer />
                    Promociones disponibles.
                    <CustomButton
                      className={classes.promotionBtn}
                      onClick={openPromotionModal}
                    >
                      Ver aquí.
                    </CustomButton>
                  </div>
                </Hidden>
              )}
              <div className={classes.searchContainer}>
                <SearchBar
                  placeholder="Nombre del producto y presione enter"
                  color="white"
                  externalHandleSearch={fetchProductsCallback}
                />
              </div>
              <div className={classes.orderLeftContentData}>
                <div className={classes.sectionTitleWrapper}>
                  <Typography className={classes.sectionTitle}>
                    Agrega productos al pedido manual.
                  </Typography>
                </div>
                <Divider
                  className={classes.divider}
                  style={{marginBottom: 8}}
                />
                <div
                  className={`${classes.filters} ${
                    isFetchingProducts ? classes.filterDisabled : ''
                  }`}
                >
                  <span className={classes.filterText}>Filtrar por:</span>
                  <LinkButton
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setCategoryFilter();
                      resetPromotionSelected();
                    }}
                    className={`${classes.filterBtn} ${
                      filterActive === 'category' ? classes.filterBtnActive : ''
                    }`}
                    disabled={isFetchingProducts}
                  >
                    Categorias
                  </LinkButton>
                  <LinkButton
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setPromotionFilter();
                      resetCategoriesSelected();
                    }}
                    className={`${classes.filterBtn} ${
                      filterActive === 'promotion'
                        ? classes.filterBtnActive
                        : ''
                    }`}
                    disabled={isFetchingProducts}
                  >
                    Promociones
                  </LinkButton>
                </div>

                {filterActive === 'category' &&
                  categories &&
                  !!categories.length && (
                    <div className={classes.categoriesContainer}>
                      <HorizontalFilter>
                        <HorizontalScroll>
                          <FilterButton
                            id="all"
                            handleOnClick={resetCategoriesSelected}
                            selected={categoriesSelected.length === 0}
                            text="todos"
                            disabled={isFetchingProducts}
                          />

                          {categories.map((c: Category, index: number) => (
                            <FilterButton
                              key={`category-${index}`}
                              selected={categoriesSelected.indexOf(c.id) !== -1}
                              id={c.id}
                              handleOnClick={handleCategoryOption}
                              text={c.name}
                              disabled={isFetchingProducts}
                            />
                          ))}
                        </HorizontalScroll>
                      </HorizontalFilter>
                    </div>
                  )}

                {filterActive === 'promotion' && storePromotions && (
                  <div className={classes.categoriesContainer}>
                    <HorizontalFilter>
                      <HorizontalScroll>
                        <FilterButton
                          id="all"
                          selected={promotionSelected.length === 0}
                          handleOnClick={resetPromotionSelected}
                          text="todos"
                          disabled={isFetchingProducts}
                        />

                        {storePromotions
                          .filter(p => p.enabled)
                          .map((p: SinglePromotion) => (
                            <FilterButton
                              key={p.id}
                              id={p.id}
                              selected={promotionSelected.includes(p.id)}
                              handleOnClick={() => {
                                handlePromotionOption(p.id);
                              }}
                              text={p.name}
                              disabled={isFetchingProducts}
                            />
                          ))}
                      </HorizontalScroll>
                    </HorizontalFilter>
                  </div>
                )}
                <div className={classes.productList} ref={rootRef}>
                  {products && products.length ? (
                    <>
                      {products
                        // .filter(p => p.enabled)
                        .map((product, i) => {
                          const isInPromotion =
                            product &&
                            product.promotions &&
                            product.promotions.length;

                          const outOfStock = productOutOfStock(product);

                          return (
                            <ProductItem
                              key={`product-${i}`}
                              currency={currency}
                              product={product}
                              isInPromotion={!!isInPromotion}
                              outOfStock={outOfStock}
                              onAdd={() => handleAddProductToCart(product, 1)}
                            />
                          );
                        })}
                      {isFetchingProducts ? (
                        <div className={classes.loadingSpinnerWrap}>
                          <CircularProgress
                            className={classes.loadingSpinner}
                          />
                        </div>
                      ) : null}
                    </>
                  ) : (
                    !isFetchingProducts && (
                      <div style={{margin: '36px auto'}}>
                        No se encontraron productos
                      </div>
                    )
                  )}

                  <div ref={nearScreenRef}></div>
                </div>

                {isFetchingProducts && !(products && products.length > 0) && (
                  <div className={classes.loadingSpinnerWrap}>
                    <CircularProgress className={classes.loadingSpinner} />
                  </div>
                )}
              </div>
            </section>
            <Hidden xsDown>
              <div className={classes.orderRightContent}>
                <div className={classes.sectionTitleSummary}>
                  <Typography className={classes.sectionTitle}>
                    Resumen de Productos
                  </Typography>
                  <div className={classes.shoppingCartItemsAmount}>
                    {shoppingCartItemsAmount}
                  </div>
                </div>
                <div className={classes.orderContent}>
                  {shoppingCartItems ? (
                    <>
                      {shoppingCartItems}
                      <ShoppingCartFooter
                        coupon={coupon}
                        couponDiscount={couponDiscount}
                        currency={currency}
                        promotionsApplied={promotionsApplied}
                        shoppingCart={shoppingCart}
                        subTotal={subTotal}
                        total={total}
                        handleGlobalDiscount={handleGlobalDiscount}
                        handleCouponApply={handleCouponApply}
                        handleCouponRemove={handleCouponRemove}
                        globalDiscount={globalDiscountData}
                        handleRemoveGlobalDiscount={
                          removeGlobalDiscountShoppingCart
                        }
                      />
                    </>
                  ) : (
                    <div style={{margin: '36px auto'}}>
                      El carrito de compras está vacío
                    </div>
                  )}
                </div>
                {shoppingCartItems && (
                  <div className={classes.orderContent} style={{marginTop: 16}}>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      className={classes.checkoutButton}
                      onClick={() => {
                        resetCategoriesSelected();
                        history.push('/orders/manual-order/checkout/info');
                      }}
                      disabled={!total || !subTotal}
                    >
                      Continuar
                    </CustomButton>
                  </div>
                )}
                {storePromotions && storePromotions.length > 0 && (
                  <div className={classes.promotion}>
                    <LocalOffer />
                    Promociones disponibles.
                    <CustomButton
                      className={classes.promotionBtn}
                      onClick={openPromotionModal}
                    >
                      Ver aquí.
                    </CustomButton>
                  </div>
                )}
              </div>
            </Hidden>
            <Hidden smUp>
              {shoppingCartItems && (
                <div
                  className={shopCartMobileClasses}
                  style={{
                    height: shopCartOpen ? `${windowHeight - 80}px` : undefined,
                  }}
                >
                  {shopCartOpen ? (
                    <>
                      <div className={classes.sectionTitleSummary}>
                        <Typography className={classes.sectionTitle}>
                          Resumen de Productos
                        </Typography>
                        <div className={classes.shoppingCartItemsAmount}>
                          {shoppingCartItemsAmount}
                        </div>
                        <IconButton
                          className={classes.footerMobileCollapse}
                          onClick={closeShopCart}
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                      </div>
                      {shoppingCartItems}
                      <ShoppingCartFooter
                        couponDiscount={couponDiscount}
                        coupon={coupon}
                        currency={currency}
                        promotionsApplied={promotionsApplied}
                        shoppingCart={shoppingCart}
                        subTotal={subTotal}
                        total={total}
                        handleGlobalDiscount={handleGlobalDiscount}
                        handleCouponApply={handleCouponApply}
                        handleCouponRemove={handleCouponRemove}
                        globalDiscount={globalDiscountData}
                        handleRemoveGlobalDiscount={
                          removeGlobalDiscountShoppingCart
                        }
                      />
                    </>
                  ) : (
                    <>
                      <div className={classes.row}>
                        <Typography
                          variant="body2"
                          className={classes.footerMobileText}
                        >
                          Tienes {shoppingCartItemsAmount} productos en tu
                          carrito
                        </Typography>
                        <IconButton
                          className={classes.footerMobileCollapse}
                          onClick={openShopCart}
                        >
                          <KeyboardArrowUp />
                        </IconButton>
                      </div>
                      <div className={classes.row}>
                        <Typography className={classes.shoppingCartTotal}>
                          Total
                        </Typography>
                        <Typography className={classes.shoppingCartTotal}>
                          {total && shoppingCart.items.length
                            ? commaFormat(total, currency.symbol)
                            : '--'}
                        </Typography>
                      </div>
                    </>
                  )}
                  <CustomButton
                    variant="contained"
                    color="primary"
                    className={classes.checkoutButtonMobile}
                    onClick={() => {
                      resetCategoriesSelected();
                      history.push('/orders/manual-order/checkout/info');
                    }}
                  >
                    Continuar
                  </CustomButton>
                </div>
              )}
            </Hidden>
          </main>
          {selectedProduct && (
            <ProductConfigurationModal
              open={productConfiguratorModalOpen}
              product={selectedProduct}
              onAddToCart={handleAddToCartFromConfigurationModal}
              onClose={handleModalConfigurationClose}
            />
          )}
          {storePromotions && (
            <PromotionModal
              open={promotionModalOpen}
              promotions={storePromotions}
              onClose={closePromotionModal}
              currency={currency}
              onViewProducts={handleSeePromotionProducts}
            />
          )}
        </Route>
        <Route path="/orders/manual-order/checkout">
          <AdditionalInformationProvider>
            <ManualOrderCheckout />
          </AdditionalInformationProvider>
        </Route>
        <Route exact path="/orders/manual-order/success">
          <OrderCreationSuccess
            createOrderSuccess={!!createOrderSuccess}
            order={order}
            orderLink={orderLink}
            resetState={resetState}
          />
        </Route>
      </Switch>
      {/* <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => setTierRestrictionModal(false)}
      /> */}
    </div>
  );
}
